import React from "react";

/**
 * BeforeAfter comparison slider.
 * - `before` and `after` are image src urls.
 * - `label` optional overlay label.
 */
export default function BeforeAfter({ before, after, label = "Before / After", className = "", testId = "before-after" }) {
  const containerRef = React.useRef(null);
  const [pos, setPos] = React.useState(50); // percentage
  const draggingRef = React.useRef(false);

  const setPercentFromClientX = React.useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(4, Math.min(96, p)));
  }, []);

  const onDown = (e) => {
    draggingRef.current = true;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    setPercentFromClientX(cx);
  };
  const onMove = React.useCallback((e) => {
    if (!draggingRef.current) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    setPercentFromClientX(cx);
  }, [setPercentFromClientX]);
  const onUp = React.useCallback(() => { draggingRef.current = false; }, []);

  React.useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [onMove, onUp]);

  return (
    <div
      ref={containerRef}
      className={`relative select-none overflow-hidden bg-black ${className}`}
      data-testid={testId}
      onMouseDown={onDown}
      onTouchStart={onDown}
      style={{ aspectRatio: "16/10" }}
    >
      {/* AFTER (base) */}
      <img
        src={after}
        alt="After — professional listing photo"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      {/* BEFORE (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `polygon(0 0, ${pos}% 0, ${pos}% 100%, 0 100%)` }}
      >
        <img
          src={before}
          alt="Before — rushed listing photo"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </div>

      {/* labels */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 flex flex-col gap-2 z-10">
        <span className="inline-block px-2.5 py-1 text-[10px] tracking-[0.24em] uppercase bg-black/70 text-white font-medium">
          Before
        </span>
      </div>
      <div className="absolute top-4 right-4 md:top-6 md:right-6 flex flex-col gap-2 items-end z-10">
        <span className="inline-block px-2.5 py-1 text-[10px] tracking-[0.24em] uppercase bg-white text-black font-medium">
          After
        </span>
      </div>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 z-20"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px bg-white/90" />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_0_24px_rgba(10,181,255,0.6)] flex items-center justify-center cursor-ew-resize"
          data-testid={`${testId}-handle`}
        >
          <svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden>
            <path d="M6 2 2 6l4 4" stroke="#000" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 2l4 4-4 4" stroke="#000" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      {label && (
        <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 z-10">
          <span className="text-white text-[11px] tracking-[0.24em] uppercase font-medium bg-black/40 backdrop-blur px-2.5 py-1">
            {label}
          </span>
        </div>
      )}
    </div>
  );
}
